import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";

/**
 * Brand button — implements the T-REX button physics (radial gradient at 30/30,
 * inset 1px white highlight top + dark line bottom, 4px radius, optional
 * champagne bloom on dark grounds).
 *
 * Variants:
 *   - champagne (default — sovereign / read / attest moments)
 *   - lava                — transformation / write / settle / mint moments
 *   - ghost               — pairs as a secondary
 *
 * Tone: "day" (on bone surfaces) or "night" (on aubergine surfaces).
 *
 * Renders as <button>, <Link to>, or <a href> depending on props.
 */
export const BrandButton = forwardRef(function BrandButton(
  {
    variant = "champagne",
    tone = "night",
    to,
    href,
    external = false,
    size = "md",
    icon = true,         // show trailing arrow / external icon
    children,
    className = "",
    "data-testid": testId,
    ...rest
  },
  ref,
) {
  const variantCls = `btn-${variant}-${tone}`;
  const sizeCls = size === "sm" ? "btn-sm" : "";
  const cls = `btn-brand ${variantCls} ${sizeCls} ${className}`.trim();

  const inner = (
    <>
      {children}
      {icon && (external
        ? <ExternalLink className="arrow h-3.5 w-3.5" />
        : <ArrowRight className="arrow h-3.5 w-3.5" />
      )}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={cls} data-testid={testId} {...rest}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={cls}
        data-testid={testId}
        {...rest}
      >
        {inner}
      </a>
    );
  }
  return (
    <button ref={ref} type="button" className={cls} data-testid={testId} {...rest}>
      {inner}
    </button>
  );
});
